import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { useTheme } from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { CheckInIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/CheckInIcon'
import { CheckMarkInCircleIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/CheckMarkInCircleIcon'
import { CircleArrowIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/CircleArrowIcon'
import { EditIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/EditIcon'
import { NotesIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/NotesIcon'
import { TaskIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/TaskIcon'
import { SecondaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/secondaryButton/SecondaryButton'
import { CheckboxFilterSection } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/taskFilterMenu/TaskFilterMenu'
import { TaskListItemProps } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/taskListItem/TaskListItem'
import { TaskListPanel } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/taskListPanel/TaskListPanel'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { actions as mixpanelActions, mixpanelEvents } from '../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import { getClientFullName } from '../../../common/utils/utils'
import { TslReviewModal } from '../../../lyraTherapy/treatmentSummaryLetter/components/TslReviewModal'
import { DaysOldLabel, getDaysOld } from '../components/DaysOldLabel'
import { getSessionTypeLabel } from '../components/getSessionTypeLabel'
import { InvoiceDeadlineBanner } from '../components/InvoiceDeadlineBanner'
import { OVERDUE_TASK_THRESHOLD_DAYS } from '../constants'
import { useManualTaskActions } from '../hooks/useManualTaskActions'
import { useNavigateToCloseSession } from '../hooks/useNavigateToCloseSession'
import { OpenItemsSortOrder, useOpenItemsFilterSections } from '../hooks/useOpenItemsFilterSections'
import { useSharedItemHoverActions } from '../hooks/useSharedItemHoverActions'
import { ApptTaskItem, ManualTaskItem, TaskItem, TslTaskItem } from '../types'

enum TaskTypeFilter {
  INVOICES = 'invoices',
  NOTES = 'notes',
  MANUAL = 'manual',
  SUMMARY_LETTERS = 'summary_letters',
}

const filterPredicates: Record<TaskTypeFilter, (task: TaskItem) => boolean> = {
  [TaskTypeFilter.INVOICES]: (task) => task.kind === 'appointment' && task.needsInvoice,
  [TaskTypeFilter.NOTES]: (task) => task.kind === 'appointment' && !task.needsInvoice,
  [TaskTypeFilter.MANUAL]: (task) => task.kind === 'manual',
  [TaskTypeFilter.SUMMARY_LETTERS]: (task) => task.kind === 'tsl',
}

const isOverdueAppointmentTask = (task: TaskItem): boolean =>
  task.kind === 'appointment' && task.date !== null && getDaysOld(task.date) >= OVERDUE_TASK_THRESHOLD_DAYS

type TasksPanelProps = {
  tasks: TaskItem[]
  isLoading: boolean
  onItemCountChange: (count: number) => void
}

const TasksPanel: React.FC<TasksPanelProps> = ({ tasks, isLoading, onItemCountChange }) => {
  const intl = useIntl()
  const { isPreferredNameEnabled, isTSLEnabled } = useFlags()
  const { sharedSections, clearSharedFilters, applyFilters } = useOpenItemsFilterSections(
    'tasks',
    OpenItemsSortOrder.OLDEST,
  )
  const [taskTypeFilter, setTaskTypeFilter] = useState<TaskTypeFilter[]>([])
  const getSharedHoverActions = useSharedItemHoverActions('tasks')
  const { colors } = useTheme()
  const { closeSession, completeNote } = useNavigateToCloseSession()
  const { openEdit, markDone, openCreate, modal: manualTaskModals } = useManualTaskActions(tasks)
  const [tslLetterId, setTslLetterId] = useState<string | null>(null)

  const mapManualTask = useCallback(
    (task: ManualTaskItem): TaskListItemProps => ({
      icon: <TaskIcon size={24} />,
      taskType: <FormattedMessage defaultMessage='Created by you' description='Manual task subtitle' />,
      title: task.description,
      rightText: task.date ? <DaysOldLabel daysOld={getDaysOld(task.date)} /> : null,
      hoverActions: [
        {
          id: 'edit-manual',
          icon: <EditIcon size={20} isFilled={false} fillColor={colors.iconDefault} />,
          label: intl.formatMessage({
            defaultMessage: 'Edit',
            description: 'Manual task hover action label',
          }),
          testId: tID('TasksPanel-edit-manual'),
          onPress: () => {
            track({
              event: mixpanelEvents.BUTTON_PRESS,
              action: mixpanelActions.OPEN_ITEMS_HOVER_ACTION,
              properties: { panel: 'tasks', hover_action: 'edit_manual', origin: 'hover' },
            })
            openEdit(task.id)
          },
        },
        {
          id: 'mark-manual-done',
          icon: <CheckMarkInCircleIcon size={20} fillColor={colors.iconDefault} />,
          label: intl.formatMessage({
            defaultMessage: 'Mark as done',
            description: 'Manual task hover action label',
          }),
          testId: tID('TasksPanel-mark-manual-done'),
          onPress: () => {
            track({
              event: mixpanelEvents.BUTTON_PRESS,
              action: mixpanelActions.OPEN_ITEMS_HOVER_ACTION,
              properties: { panel: 'tasks', hover_action: 'mark_manual_done', origin: 'hover' },
            })
            markDone(task.id)
          },
        },
      ],
      testId: tID('TasksPanel-manual-item'),
      onPress: () => {
        track({
          event: mixpanelEvents.BUTTON_PRESS,
          action: mixpanelActions.OPEN_ITEMS_ROW_PRESS,
          properties: { panel: 'tasks', task_type: 'manual' },
        })
        openEdit(task.id)
      },
    }),
    [colors.iconDefault, intl, markDone, openEdit],
  )

  const mapApptTask = useCallback(
    (task: ApptTaskItem): TaskListItemProps => {
      const sessionLabel = getSessionTypeLabel(intl, task.appointment)

      const navigateToTask = () => {
        if (task.needsInvoice) {
          closeSession(task.appointment, task.client.id)
        } else {
          completeNote(task.appointment, task.client.id)
        }
      }

      const taskType = task.needsInvoice ? 'appointment_invoice' : 'appointment_note'

      return {
        icon: task.needsInvoice ? <CheckInIcon size={24} fillColor={colors.iconDefault} /> : <NotesIcon size={24} />,
        taskType: task.needsInvoice ? (
          <FormattedMessage defaultMessage='Complete invoice and note' description='Task type label' />
        ) : (
          <FormattedMessage defaultMessage='Complete note' description='Task type label' />
        ),
        title: `${getClientFullName(task.client, isPreferredNameEnabled)} • ${sessionLabel}`,
        rightText: task.date ? <DaysOldLabel daysOld={getDaysOld(task.date)} /> : null,
        hoverActions: [
          ...getSharedHoverActions(task.client),
          {
            id: 'go-to-item',
            icon: <CircleArrowIcon size={20} />,
            label: intl.formatMessage({
              defaultMessage: 'Go to item',
              description: 'Task hover action to navigate to the session',
            }),
            testId: tID('TasksPanel-go-to-item'),
            onPress: () => {
              track({
                event: mixpanelEvents.BUTTON_PRESS,
                action: mixpanelActions.OPEN_ITEMS_HOVER_ACTION,
                properties: { panel: 'tasks', hover_action: 'go_to_item', origin: 'hover' },
              })
              navigateToTask()
            },
          },
        ],
        testId: tID('TasksPanel-item'),
        onPress: () => {
          track({
            event: mixpanelEvents.BUTTON_PRESS,
            action: mixpanelActions.OPEN_ITEMS_ROW_PRESS,
            properties: { panel: 'tasks', task_type: taskType },
          })
          navigateToTask()
        },
      }
    },
    [intl, colors.iconDefault, isPreferredNameEnabled, getSharedHoverActions, closeSession, completeNote],
  )

  const mapTslTask = useCallback(
    (task: TslTaskItem): TaskListItemProps => ({
      icon: <NotesIcon size={24} />,
      taskType: <FormattedMessage defaultMessage='Treatment Summary Letter' description='TSL task type label' />,
      title: `${task.clientName} • ${task.dateRange}`,
      rightText: task.date ? <DaysOldLabel daysOld={getDaysOld(task.date)} /> : null,
      testId: tID('TasksPanel-tsl-item'),
      hoverActions: [],
      onPress: () => {
        track({
          event: mixpanelEvents.BUTTON_PRESS,
          action: mixpanelActions.OPEN_ITEMS_ROW_PRESS,
          properties: { panel: 'tasks', task_type: 'treatment_summary_letter' },
        })
        setTslLetterId(task.letterId)
      },
    }),
    [],
  )

  const taskTypeSection: CheckboxFilterSection = {
    type: 'checkbox',
    label: <FormattedMessage defaultMessage='Filter by Task' description='Filter section label' />,
    options: [
      {
        id: TaskTypeFilter.INVOICES,
        text: intl.formatMessage({ defaultMessage: 'Invoices', description: 'Task type filter option' }),
      },
      {
        id: TaskTypeFilter.NOTES,
        text: intl.formatMessage({ defaultMessage: 'Notes', description: 'Task type filter option' }),
      },
      {
        id: TaskTypeFilter.MANUAL,
        text: intl.formatMessage({ defaultMessage: 'Personal tasks', description: 'Task type filter option' }),
      },
      ...(isTSLEnabled
        ? [
            {
              id: TaskTypeFilter.SUMMARY_LETTERS,
              text: intl.formatMessage({
                defaultMessage: 'Treatment Summary letters',
                description: 'Task type filter option',
              }),
            },
          ]
        : []),
    ],
    value: taskTypeFilter,
    onChange: (values: string[]) => {
      const next = values as TaskTypeFilter[]
      setTaskTypeFilter(next)
      track({
        event: mixpanelEvents.OPEN_ITEMS_FILTER_CHANGE,
        properties: { panel: 'tasks', filter: 'task_type', value: next },
      })
    },
  }

  const filterSections = [...sharedSections, taskTypeSection]

  const filteredTasks =
    taskTypeFilter.length === 0
      ? tasks
      : tasks.filter((task) => taskTypeFilter.some((filter) => filterPredicates[filter](task)))

  const taskItems = useMemo(
    () =>
      applyFilters(filteredTasks).map<TaskListItemProps>((task) => {
        if (task.kind === 'manual') return mapManualTask(task)
        if (task.kind === 'tsl') return mapTslTask(task)
        return mapApptTask(task)
      }),
    [applyFilters, filteredTasks, mapManualTask, mapApptTask, mapTslTask],
  )

  useEffect(() => {
    onItemCountChange(taskItems.length)
  }, [onItemCountChange, taskItems.length])

  const overdueInvoiceCount = useMemo(() => tasks.filter(isOverdueAppointmentTask).length, [tasks])

  const clearFilters = () => {
    clearSharedFilters()
    setTaskTypeFilter([])
  }

  return (
    <>
      <TaskListPanel
        filterSections={filterSections}
        items={taskItems}
        isLoading={isLoading}
        banner={overdueInvoiceCount > 0 ? <InvoiceDeadlineBanner count={overdueInvoiceCount} /> : undefined}
        loadingMessage={<FormattedMessage defaultMessage='Loading tasks...' description='Loading state message' />}
        filteredEmptyMessage={
          <FormattedMessage
            defaultMessage='No tasks found, remove filters to see more.'
            description='Filtered empty state message'
          />
        }
        onClearFilters={clearFilters}
        headerRight={
          <SecondaryButton
            text={<FormattedMessage defaultMessage='Add task' description='Button to open the add manual task modal' />}
            onPress={() => {
              track({
                event: mixpanelEvents.BUTTON_PRESS,
                action: mixpanelActions.OPEN_ITEMS_ADD_TASK,
              })
              openCreate()
            }}
            testID={tID('TasksPanel-add-task-button')}
          />
        }
      />
      {manualTaskModals}
      <TslReviewModal letterId={tslLetterId} onClose={() => setTslLetterId(null)} />
    </>
  )
}

export default TasksPanel
